import React from "react";
import { useSearchParams } from "react-router-dom";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import {
  parseFilterModelFromSearchParams,
  parseSortModel,
  filterModelToSearchParams,
  serializeSortModel,
} from "common/graphql/queryString";
import { DateRangeFilter, TransactionOrderBy } from "common/graphql/queries/GetCurbsuiteTransactions";
import { CurbPassTransactionsTableVariant } from "./types";

export const PAGE_SIZE = 1000;

const FIELD_TO_ORDER_BY: Record<string, (dir: "asc" | "desc") => TransactionOrderBy> = {
  transactionIndex: (dir) => ({ created_at: dir }),
  transactionDate: (dir) => ({ transaction_date: dir }),
  timeRange: (dir) => ({ end_time: dir }),
  transactionAssignedDate: (dir) => ({ assigned_to_account_at_local: dir }),
};

export interface TransactionsUrlState {
  page: number;
  handlePageChange: (newPage: number) => void;
  sortModel: GridSortModel;
  handleSortModelChange: (newSortModel: GridSortModel) => void;
  orderBy: TransactionOrderBy[];
  appliedFilters: GridFilterModel | undefined;
  filterModel: GridFilterModel | undefined;
  handleApplyFilters: (newFilterModel: GridFilterModel) => void;
  handleFilterModelChange: (newFilterModel: GridFilterModel) => void;
  now: Date;
  selectedDate: Date;
  setSelectedDate: (newDate: Date) => void;
  dateRangeFilter: DateRangeFilter;
}

/**
 * Pagination, sort, filter, and month-picker state, all synced to the URL's search params. Doesn't
 * know about columns or which query backs the grid -- every shape (park/wallet, bus-station,
 * airport) uses this exact same state shape.
 */
export function useTransactionsUrlState({
  defaultSort,
  isBusStation,
  variant,
}: {
  defaultSort: string;
  isBusStation: boolean;
  variant: CurbPassTransactionsTableVariant;
}): TransactionsUrlState {
  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: defaultSort,
  });

  const [now] = React.useState(new Date());
  const selectedDate = new Date(searchParams.get("date") || startOfMonth(now));
  const setSelectedDate = (newDate: Date) => {
    searchParams.set("date", newDate.toISOString());
    setSearchParams(searchParams, { replace: true });
  };

  const selectedEpoch = selectedDate.getTime();
  const [since, until] = React.useMemo(() => [startOfMonth(selectedEpoch), endOfMonth(selectedEpoch)], [selectedEpoch]);

  // Parsed from the two filter params alone (not the whole searchParams object), so the parsed
  // model -- and every query filter memoized off it -- stays referentially stable across page/sort
  // changes and only changes when the applied filter does.
  const filterParam = searchParams.get("filter");
  const filterOperatorParam = searchParams.get("filterOperator");
  const appliedFilters = React.useMemo(() => {
    const filterParams = new URLSearchParams();
    if (filterParam) {
      filterParams.set("filter", filterParam);
    }
    if (filterOperatorParam) {
      filterParams.set("filterOperator", filterOperatorParam);
    }
    return parseFilterModelFromSearchParams(filterParams);
  }, [filterParam, filterOperatorParam]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);
  // Keep the grid's filter panel in step when the applied filter changes from outside it -- an
  // adjustment's "Adjusts #" link (AUT-6163) rewrites the filter (and month) params on the same
  // route, which doesn't remount the table. Edits the user hasn't applied yet don't touch the URL,
  // and the memo above only changes when the filter params do, so page/sort changes don't clobber
  // them.
  React.useEffect(() => {
    setFilterModel(appliedFilters);
  }, [appliedFilters]);

  const dateRangeFilter: DateRangeFilter = React.useMemo(() => {
    // Bus stations date by operator assignment (a reviewer attributes the session during citation
    // review). Airports must NOT: a trip charge sets account_id at creation, so the insert trigger
    // stamps assigned_to_account_at with the generator/backfill run time -- dating a July trip
    // backfilled in August as August, and hiding it from the July month picker entirely.
    // transaction_date carries the capture date and is the correct field for them.
    const field = isBusStation && variant === "park" ? "assigned_to_account_at_local_date" : "transaction_date";
    return { [field]: { _gte: since, _lt: until } } as DateRangeFilter;
  }, [isBusStation, variant, since, until]);

  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;

    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  const sort = searchParams.get("sort") || defaultSort;
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  const orderBy: TransactionOrderBy[] = React.useMemo(() => {
    const { field: sortField, sort: sortDirection } = sortModel[0];
    const direction = sortDirection || "desc";

    const toOrderBy = FIELD_TO_ORDER_BY[sortField] || FIELD_TO_ORDER_BY.transactionIndex;
    return [toOrderBy(direction), { transaction_id: "desc" }];
  }, [sortModel]);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      // Manually apply filters when user clears all filters
      handleApplyFilters(newFilterModel);
    }

    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  return {
    page,
    handlePageChange,
    sortModel,
    handleSortModelChange,
    orderBy,
    appliedFilters,
    filterModel,
    handleApplyFilters,
    handleFilterModelChange,
    now,
    selectedDate,
    setSelectedDate,
    dateRangeFilter,
  };
}
